import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useListProductSources,
  type ProductSource,
} from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { isEplanSelectableProductSource } from '@/features/product-catalog/utils/isRagIngestedProductSource'

type EPlanSelectDialogProps = {
  productId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedSourceId: string | null
  onConfirm: (source: ProductSource) => void
}

export function EPlanSelectDialog(props: EPlanSelectDialogProps) {
  const { productId, open, onOpenChange, selectedSourceId, onConfirm } = props
  const { t } = useTranslation('machineryDirectiveCompliance')
  const {
    data: sources = [],
    isLoading,
    isError,
  } = useListProductSources(open ? productId : undefined)

  const selectableSources = useMemo(
    () => sources.filter((source) => isEplanSelectableProductSource(source)),
    [sources]
  )

  const [pendingSourceId, setPendingSourceId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    if (
      selectedSourceId != null &&
      selectableSources.some((source) => source.id === selectedSourceId)
    ) {
      setPendingSourceId(selectedSourceId)
      return
    }

    const firstSource = selectableSources[0]
    if (firstSource != null) {
      setPendingSourceId(firstSource.id)
      return
    }

    setPendingSourceId(null)
  }, [open, selectedSourceId, selectableSources])

  const handleConfirm = () => {
    if (pendingSourceId == null) {
      return
    }

    const selectedSource = selectableSources.find(
      (source) => source.id === pendingSourceId
    )
    if (selectedSource == null) {
      return
    }

    onConfirm(selectedSource)
    onOpenChange(false)
  }

  const showLoading = isLoading
  const showError = !isLoading && isError
  const showEmpty = !isLoading && !isError && selectableSources.length === 0
  const showSourceList = !isLoading && !isError && selectableSources.length > 0
  const confirmDisabled = pendingSourceId == null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {t('settings.ePlan.selectSourcesDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('settings.ePlan.selectSourcesDialog.description')}
          </DialogDescription>
        </DialogHeader>

        {showLoading && (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='size-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {showError && (
          <p className='text-sm text-destructive'>
            {t('settings.ePlan.selectSourcesDialog.loadError')}
          </p>
        )}

        {showEmpty && (
          <p className='text-sm text-muted-foreground'>
            {t('settings.ePlan.selectSourcesDialog.empty')}
          </p>
        )}

        {showSourceList && (
          <RadioGroup
            value={pendingSourceId ?? ''}
            onValueChange={setPendingSourceId}
            className='max-h-64 overflow-y-auto pe-1'
          >
            {selectableSources.map((source) => {
              const radioId = `machinery-e-plan-source-${source.id}`
              return (
                <div
                  key={source.id}
                  className='flex items-start gap-3 rounded-lg border p-3'
                >
                  <RadioGroupItem
                    value={source.id}
                    id={radioId}
                    className='mt-0.5'
                  />
                  <Label
                    htmlFor={radioId}
                    className='min-w-0 flex-1 cursor-pointer space-y-1 font-normal'
                  >
                    <span className='block text-sm font-medium text-foreground'>
                      {source.name}
                    </span>
                    {source.classification != null && (
                      <span className='block text-xs text-muted-foreground'>
                        {source.classification.replace(/_/g, ' ')}
                      </span>
                    )}
                  </Label>
                </div>
              )
            })}
          </RadioGroup>
        )}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('settings.ePlan.selectSourcesDialog.cancel')}
          </Button>
          <Button
            type='button'
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {t('settings.ePlan.selectSourcesDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
